import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProgressRing from "@/components/ProgressRing";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  BookOpenCheck, FileSpreadsheet, CalendarClock, Megaphone,
  Sparkles, ArrowUpRight, CalendarDays, Sun, Award, Clock,
} from "lucide-react";

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
};

const relativeDate = (dateStr) => {
  const n = daysUntil(dateStr);
  if (n === null) return "";
  if (n === 0) return "Today";
  if (n === 1) return "Tomorrow";
  if (n < 0) return `${Math.abs(n)} day${n === -1 ? "" : "s"} ago`;
  return `in ${n} days`;
};

export default function StudentDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/student/dashboard");
        setData(data);
      } catch {}
    })();
  }, []);

  const greet = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  if (!data) {
    return <div className="text-sm text-muted-foreground animate-pulse">Loading your day…</div>;
  }

  return (
    <div className="space-y-8" data-testid="student-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">// student workspace</div>
          <h1 className="mt-1 text-4xl md:text-5xl font-bold tracking-tight">
            {greet()}, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="mt-1 text-muted-foreground font-serif-display">
            {data.next_homework
              ? `Your next task: ${data.next_homework.title} — ${relativeDate(data.next_homework.due_date)}.`
              : "You're all caught up. Keep it up!"}
          </p>
        </div>
        <div className="flex gap-2">
          <Badge className="rounded-full bg-primary/10 text-primary hover:bg-primary/15 border-primary/20">Term 1 · 2026</Badge>
          <Badge variant="outline" className="rounded-full">
            <Sun className="h-3 w-3 mr-1" /> {data.today_day}
          </Badge>
        </div>
      </div>

      {/* Progress rings */}
      <Card className="rounded-2xl">
        <CardContent className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ProgressRing
              testId="ring-attendance"
              value={data.attendance.pct}
              label="Attendance"
              sublabel={`${data.attendance.present + data.attendance.late}/${data.attendance.total} days`}
            />
            <ProgressRing
              testId="ring-homework"
              value={data.homework.pct}
              label="Homework"
              sublabel={`${data.homework.submitted}/${data.homework.total} submitted`}
              color="hsl(var(--chart-2))"
            />
            <ProgressRing
              testId="ring-marks"
              value={data.avg_marks_pct}
              label="Term average"
              sublabel="all subjects"
              color="hsl(var(--chart-3))"
            />
          </div>
        </CardContent>
      </Card>

      {/* Next assignment + next exam */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card className="rounded-2xl lift" data-testid="next-homework-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary grid place-items-center">
                <BookOpenCheck className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Next assignment</div>
                <CardTitle className="text-lg">
                  {data.next_homework ? data.next_homework.title : "Nothing due"}
                </CardTitle>
              </div>
            </div>
            {data.next_homework && (
              <Badge className="rounded-full bg-primary/10 text-primary hover:bg-primary/15 border-primary/20">
                {relativeDate(data.next_homework.due_date)}
              </Badge>
            )}
          </CardHeader>
          <CardContent>
            {data.next_homework ? (
              <>
                <div className="text-sm text-muted-foreground line-clamp-2 mb-4">
                  <span className="uppercase text-[0.65rem] tracking-widest mr-2">{data.next_homework.subject}</span>
                  {data.next_homework.description}
                </div>
                <Link to="/homework">
                  <Button size="sm" className="rounded-xl gap-1" data-testid="cta-open-homework">
                    Open homework <ArrowUpRight className="h-3.5 w-3.5" />
                  </Button>
                </Link>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">You're all caught up 🎉 Great work — check back tomorrow.</div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl lift" data-testid="next-exam-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary grid place-items-center">
                <FileSpreadsheet className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Next exam</div>
                <CardTitle className="text-lg">
                  {data.next_exam ? data.next_exam.subject : "No exam scheduled"}
                </CardTitle>
              </div>
            </div>
            {data.next_exam && (
              <Badge variant="outline" className="rounded-full">
                <CalendarClock className="h-3 w-3 mr-1" /> {data.next_exam.date}
              </Badge>
            )}
          </CardHeader>
          <CardContent>
            {data.next_exam ? (
              <>
                <div className="text-sm text-muted-foreground mb-4">
                  {data.next_exam.total_marks} marks · Term {data.next_exam.term} · {relativeDate(data.next_exam.date)}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Link to="/exams">
                    <Button size="sm" className="rounded-xl gap-1"><Award className="h-3.5 w-3.5" /> View report card</Button>
                  </Link>
                  <Link to="/ai">
                    <Button size="sm" variant="outline" className="rounded-xl gap-1">
                      <Sparkles className="h-3.5 w-3.5" /> Study with AI
                    </Button>
                  </Link>
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">Nothing on the horizon — enjoy the break.</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Today's timetable + announcements */}
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2 rounded-2xl">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-primary" /> Today's timetable
            </CardTitle>
            <Link to="/timetable" className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
              Full week <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </CardHeader>
          <CardContent>
            {data.today_timetable.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">No classes scheduled for {data.today_day}.</div>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2" data-testid="today-timetable">
                {data.today_timetable.map((s) => (
                  <div key={s.id} className="p-4 rounded-xl bg-muted/40 flex items-center gap-3 lift">
                    <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary grid place-items-center text-xs font-bold">
                      P{s.period}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">{s.subject}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" /> Period {s.period}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Megaphone className="h-4 w-4 text-primary" /> Latest news
            </CardTitle>
            <Link to="/announcements" className="text-xs text-muted-foreground hover:text-foreground">All</Link>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.announcements.length === 0 ? (
              <div className="text-sm text-muted-foreground">Nothing new.</div>
            ) : (
              data.announcements.map((a) => (
                <div key={a.id} className="p-3 rounded-xl bg-muted/40">
                  <div className="text-sm font-medium">{a.title}</div>
                  <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{a.body}</div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
